/* The Stitch Shop — theme JS
   Ports every prototype effect onto Shopify's native storefront APIs:
   Ajax Cart (/cart/*.js), predictive search (/search/suggest.json),
   product JSON (/products/{handle}.js), recommendations section render. */

(() => {
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const moneyFmt = (cents) => "₹" + (cents / 100).toLocaleString("en-IN", { maximumFractionDigits: 0 });
  const FREE_SHIP = parseInt(document.body.dataset.freeShip || "499500", 10);

  /* ── Preloader ───────────────────────────────────────────────── */
  const preloader = document.querySelector(".js-preloader");
  const hidePreloader = () => {
    if (!preloader) return;
    preloader.classList.add("is-done");
    setTimeout(() => preloader.remove(), 700);
  };
  if (preloader) {
    if (document.readyState === "complete" || document.readyState === "interactive" || reduceMotion) {
      setTimeout(hidePreloader, 150);
    } else {
      window.addEventListener("load", () => setTimeout(hidePreloader, 200));
      document.addEventListener("DOMContentLoaded", () => setTimeout(hidePreloader, 300));
      setTimeout(hidePreloader, 1200);
    }
  }

  /* ── Tickers ─────────────────────────────────────────────────── */
  document.querySelectorAll(".js-ticker").forEach((track) => {
    const clone = document.createElement("span");
    clone.className = "ticker-clone";
    clone.setAttribute("aria-hidden", "true");
    clone.innerHTML = track.innerHTML;
    track.appendChild(clone);
  });

  /* ── Header ──────────────────────────────────────────────────── */
  const header = document.querySelector(".js-header");
  if (header) {
    let lastY = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      if (y > 300 && y > lastY + 4) header.classList.add("is-hidden");
      else if (y < lastY - 4 || y < 300) header.classList.remove("is-hidden");
      lastY = y;
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
  }

  /* ── Dialog manager (drawers/modals/popups) with focus trap ──── */
  const overlay = document.querySelector(".js-drawer-overlay");
  let openDialog = null;
  let dialogUnderCustomize = null;
  let lastTrigger = null;
  const FOCUSABLE = 'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

  const getOpenDialog = () => (
    openDialog || document.querySelector(".modal.is-open, .drawer.is-open, .popup.is-open")
  );

  const closeDialog = () => {
    const d = getOpenDialog();
    if (!d || d.classList.contains("js-customize-panel")) return;
    d.classList.remove("is-open");
    setTimeout(() => { if (!d.classList.contains("is-open")) d.hidden = true; }, 460);

    const customizePanel = document.querySelector(".js-customize-panel");
    const customizeStillOpen = customizePanel?.classList.contains("is-open");

    if (d.classList.contains("js-sizechart-modal") && customizeStillOpen) {
      openDialog = customizePanel;
      customizePanel.focus({ preventScroll: true });
      return;
    }

    const stillOpen = document.querySelector(".modal.is-open, .drawer.is-open, .popup.is-open");
    if (!stillOpen) {
      if (overlay) {
        overlay.classList.remove("is-open");
        setTimeout(() => { if (!overlay.classList.contains("is-open")) overlay.hidden = true; }, 380);
      }
      document.body.classList.remove("drawer-open");
      document.querySelector("main")?.removeAttribute("inert");
      lastTrigger?.setAttribute?.("aria-expanded", "false");
      lastTrigger?.focus?.();
      openDialog = null;
      dialogUnderCustomize = null;
    } else {
      openDialog = stillOpen;
    }
  };

  const openDialogEl = (dialog, trigger) => {
    if (!dialog) return;

    const qvModal = document.querySelector(".js-quickview-modal");
    if (qvModal && dialog !== qvModal) {
      qvModal.classList.remove("is-open");
      qvModal.hidden = true;
    }

    closeDialog();
    lastTrigger = trigger || null;
    trigger?.setAttribute?.("aria-expanded", "true");
    dialog.hidden = false;
    if (overlay) overlay.hidden = false;
    void dialog.offsetWidth;
    dialog.classList.add("is-open");
    overlay?.classList.add("is-open");
    document.body.classList.add("drawer-open");
    document.querySelector("main")?.setAttribute("inert", "");
    openDialog = dialog;
    dialog.focus({ preventScroll: true });
  };

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") {
      if (document.querySelector(".js-sizechart-modal.is-open")) {
        closeDialog();
        return;
      }
      if (document.querySelector(".js-customize-panel")?.classList.contains("is-open")) closeCustomize();
      else closeDialog();
      return;
    }
    if (e.key !== "Tab" || !openDialog) return;
    const items = [...openDialog.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
    if (!items.length) return;
    const first = items[0], last = items[items.length - 1];
    if (e.shiftKey && (document.activeElement === first || document.activeElement === openDialog)) {
      e.preventDefault(); last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault(); first.focus();
    }
  });

  document.addEventListener("click", (e) => {
    const opener = e.target.closest(".js-drawer-open");
    if (opener) { e.preventDefault(); openDialogEl(document.getElementById(opener.dataset.drawer), opener); return; }
    if (e.target.closest(".js-drawer-close, .js-modal-close, .js-popup-close")) {
      const customizeOpen = document.querySelector(".js-customize-panel")?.classList.contains("is-open");
      const owner = e.target.closest(".modal, .drawer, .popup");
      if (owner?.classList.contains("js-sizechart-modal") && customizeOpen) {
        closeDialog();
        return;
      }
      if (customizeOpen) {
        closeCustomize();
        if (owner?.classList.contains("is-open")) {
          openDialog = owner;
          closeDialog();
        }
        return;
      }
      closeDialog();
      return;
    }
    if (e.target === overlay) {
      if (document.querySelector(".js-customize-panel")?.classList.contains("is-open")) closeCustomize();
      else closeDialog();
      return;
    }
  });

  /* ── Hero slideshow ──────────────────────────────────────────── */
  const slideshow = document.querySelector(".js-slideshow");
  if (slideshow) {
    const slides = [...slideshow.querySelectorAll(".hero__slide")];
    const prevBtn = slideshow.querySelector(".js-hero-prev");
    const nextBtn = slideshow.querySelector(".js-hero-next");
    const dots = [...slideshow.querySelectorAll(".js-hero-dot")];
    const DEFAULT_DURATION = 7000;
    let idx = 0, start = performance.now(), raf = 0;

    const currentDuration = () => {
      const video = slides[idx]?.querySelector("video");
      if (!video) return DEFAULT_DURATION;
      return Math.max(22000, Number.isFinite(video.duration) ? video.duration * 1000 : 0);
    };

    const syncPreview = () => {
      dots.forEach((dot, i) => {
        dot.classList.toggle("is-active", i === idx);
      });
    };

    const goTo = (n) => {
      idx = (n + slides.length) % slides.length;
      slides.forEach((s, i) => {
        const active = i === idx;
        s.classList.toggle("is-active", active);
        if (active) s.removeAttribute("inert");
        else s.setAttribute("inert", "");
        const video = s.querySelector("video");
        if (video) {
          if (active) {
            video.currentTime = 0;
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        }
      });
      start = performance.now();
      syncPreview();
    };

    prevBtn?.addEventListener("click", () => goTo(idx - 1));
    nextBtn?.addEventListener("click", () => goTo(idx + 1));

    dots.forEach((dot) => {
      dot.addEventListener("click", () => {
        const targetIdx = parseInt(dot.dataset.index, 10);
        goTo(targetIdx);
      });
    });

    if (slides.length > 1) {
      goTo(0);
      if (!reduceMotion) {
        const tick = (now) => {
          const t = Math.max(0, (now - start) / currentDuration());
          if (t >= 1) goTo(idx + 1);
          raf = requestAnimationFrame(tick);
        };
        const startLoop = () => { cancelAnimationFrame(raf); start = performance.now(); raf = requestAnimationFrame(tick); };
        if (!document.hidden) startLoop();
        document.addEventListener("visibilitychange", () => {
          if (document.hidden) cancelAnimationFrame(raf);
          else startLoop();
        });
      }
    }
  }

  /* ── Scroll reveals ──────────────────────────────────────────── */
  let revealIO;
  const observeReveals = (root) => {
    (root || document).querySelectorAll("[data-stagger-group]").forEach((group) => {
      [...group.querySelectorAll(".reveal")].forEach((el, i) => {
        if (!el.style.getPropertyValue("--animation-delay"))
          el.style.setProperty("--animation-delay", (i * 0.08).toFixed(2) + "s");
      });
    });
    (root || document).querySelectorAll("[data-stagger]").forEach((el) => {
      const sibs = [...el.parentElement.querySelectorAll("[data-stagger]")];
      el.style.setProperty("--animation-delay", (sibs.indexOf(el) * 0.12 + 0.1).toFixed(2) + "s");
    });
    if (reduceMotion) {
      (root || document).querySelectorAll(".reveal").forEach((el) => el.classList.add("in-view"));
      return;
    }
    revealIO = revealIO || new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (en.isIntersecting) { en.target.classList.add("in-view"); revealIO.unobserve(en.target); }
      });
    }, { threshold: 0.1, rootMargin: "0px 0px -5% 0px" });
    (root || document).querySelectorAll(".reveal:not(.in-view)").forEach((el) => revealIO.observe(el));
  };

  /* ── Carousels ───────────────────────────────────────────────── */
  const bindVerticalWheelPassthrough = (root = document) => {
    // Disabled to prevent custom scroll-jacking from slowing down page scrolling
  };

  document.querySelectorAll(".js-carousel").forEach((c) => {
    const track = c.querySelector(".js-carousel-track");
    const step = () => {
      const first = track.firstElementChild;
      if (!first) return 320;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 20;
      return first.getBoundingClientRect().width + gap;
    };
    c.querySelector(".js-carousel-prev")?.addEventListener("click", () => track.scrollBy({ left: -step(), behavior: reduceMotion ? "auto" : "smooth" }));
    c.querySelector(".js-carousel-next")?.addEventListener("click", () => track.scrollBy({ left: step(), behavior: reduceMotion ? "auto" : "smooth" }));
    bindVerticalWheelPassthrough(c);
  });

  /* ══════════ SHOPIFY CART (Ajax API) ══════════ */
  const renderCartFromState = (cartData) => {
    document.querySelectorAll(".js-cart-count, .js-cart-count-sr").forEach((el) => (el.textContent = cartData.item_count));
    const sub = document.querySelector(".js-cart-subtotal");
    if (sub) sub.textContent = moneyFmt(cartData.total_price);

    const fillEl = document.querySelector(".js-ship-fill");
    const labelEl = document.querySelector(".js-ship-label");
    if (fillEl && labelEl) {
      fillEl.style.width = Math.min((cartData.total_price / FREE_SHIP) * 100, 100) + "%";
      labelEl.innerHTML = cartData.total_price >= FREE_SHIP
        ? "<strong>Complimentary shipping</strong> unlocked on this order"
        : `You're <strong>${moneyFmt(FREE_SHIP - cartData.total_price)}</strong> away from complimentary shipping`;
    }

    const wrap = document.querySelector(".js-cart-items");
    if (!wrap) return;
    wrap.innerHTML = cartData.items.length ? cartData.items.map((it) => {
      const props = Object.entries(it.properties || {})
        .filter(([, v]) => v)
        .map(([k, v]) => ` · ${k}: ${v}`)
        .join("");
      const img = it.image ? it.image.replace(/(\.[a-z]{3,4})(\?|$)/, "_200x$1$2") : "";
      return `
      <div class="cart-item">
        <a class="cart-item__thumb" href="${it.url}">${img ? `<img src="${img}" alt="" loading="lazy">` : ""}</a>
        <div>
          <p class="cart-item__title"><a href="${it.url}">${it.product_title}</a></p>
          <p class="cart-item__variant">${it.variant_title && it.variant_title !== "Default Title" ? it.variant_title : ""}${props}</p>
          <div class="cart-item__row">
            <span class="qty">
              <button data-cart-dec="${it.key}" data-qty="${it.quantity}" aria-label="Decrease quantity">−</button>
              <output aria-label="Quantity">${it.quantity}</output>
              <button data-cart-inc="${it.key}" data-qty="${it.quantity}" aria-label="Increase quantity">＋</button>
            </span>
            <span class="cart-item__price">${moneyFmt(it.final_line_price)}</span>
          </div>
          <button class="cart-item__remove" data-cart-remove="${it.key}">Remove</button>
        </div>
      </div>`;
    }).join("") : '<p class="cart-drawer__empty">Your cart is empty.</p>';
  };

  const refreshCart = () => fetch("/cart.js").then((r) => r.json()).then(renderCartFromState);

  const changeLine = (key, quantity) =>
    fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: key, quantity }),
    }).then((r) => r.json()).then(renderCartFromState);

  document.addEventListener("click", (e) => {
    const inc = e.target.closest("[data-cart-inc]");
    const dec = e.target.closest("[data-cart-dec]");
    const rem = e.target.closest("[data-cart-remove]");
    if (inc) changeLine(inc.dataset.cartInc, +inc.dataset.qty + 1);
    if (dec) changeLine(dec.dataset.cartDec, Math.max(0, +dec.dataset.qty - 1));
    if (rem) changeLine(rem.dataset.cartRemove, 0);
  });

  /* cart note */
  document.querySelector(".js-cart-note")?.addEventListener("change", (e) => {
    fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ note: e.target.value }),
    });
  });

  /* intercept ANY add-to-cart form (PDP, look, quick view, hotspots) */
  document.addEventListener("submit", (e) => {
    const form = e.target.closest('form[action*="/cart/add"], .js-product-form, .js-look-form, .js-qv-form');
    if (!form) return;
    e.preventDefault();
    const btn = form.querySelector('[type="submit"]');
    btn && (btn.disabled = true);

    const fd = new FormData(form);
    const monoVal = (fd.get("properties[Monogram]") || "").toString().trim();
    if (!monoVal) {
      fd.delete("properties[Monogram]");
      fd.delete("properties[Monogram style]");
      fd.delete("properties[Monogram placement]");
    }

    fetch("/cart/add.js", { method: "POST", body: fd })
      .then((r) => r.json())
      .then((res) => {
        if (res.status && res.status !== 200) throw new Error(res.description || "Could not add to cart");
        return refreshCart();
      })
      .then(() => openDialogEl(document.getElementById("cartDrawer"), btn))
      .catch((err) => alert(err.message))
      .finally(() => btn && (btn.disabled = false));
  });

  /* ══════════ PDP: variant resolution from pills ══════════ */
  document.querySelectorAll(".js-product-form").forEach((form) => {
    const jsonEl = form.querySelector(".js-product-json");
    if (!jsonEl) return;
    let variants = [];
    try { variants = JSON.parse(jsonEl.textContent); } catch (err) { return; }
    const idInput = form.querySelector(".js-variant-id");
    const priceEl = document.querySelector(".js-price");
    const atc = form.querySelector(".js-atc");

    const luxeSizing = form.querySelector(".js-luxe-sizing");
    if (luxeSizing) {
      const customPane = luxeSizing.querySelector(".js-custom-sizing");
      const methodInputs = [...luxeSizing.querySelectorAll('input[name="properties[Measurement method]"]')];
      const sizeOption = form.querySelector(".pdp__option--size");
      const syncSizing = () => {
        const mode = luxeSizing.querySelector('input[name="properties[Sizing option]"]:checked')?.value;
        const isCustom = mode === "Custom size";
        if (customPane) customPane.hidden = !isCustom;
        if (sizeOption) sizeOption.hidden = isCustom;
        methodInputs.forEach((input) => { input.disabled = !isCustom; });
        if (isCustom && !methodInputs.some((input) => input.checked) && methodInputs[0]) methodInputs[0].checked = true;
      };
      luxeSizing.addEventListener("change", (e) => {
        if (e.target.matches('input[name="properties[Sizing option]"]')) syncSizing();
      });
      syncSizing();
    }

    form.addEventListener("change", (e) => {
      if (!e.target.matches('input[name^="options["]')) return;
      const chosen = [...form.querySelectorAll('input[name^="options["]:checked')].map((i) => i.value);
      const match = variants.find((v) => chosen.every((val, i) => v[`option${i + 1}`] === val));
      const out = e.target.closest(".pdp__option")?.querySelector("output");
      if (out) out.textContent = e.target.value;
      const colorLabel = form.querySelector(".js-color-label");
      if (colorLabel && /colou?r/i.test(e.target.name)) colorLabel.textContent = e.target.value;
      if (match) {
        idInput.value = match.id;
        if (priceEl) priceEl.textContent = moneyFmt(match.price);
        if (atc) {
          atc.disabled = !match.available;
          atc.textContent = match.available ? "Add to basket" : "Sold out";
        }
      }
    });

    /* property pill outputs (Collar/Cuff/Fit) */
    form.addEventListener("change", (e) => {
      if (!e.target.matches('input[name^="properties["]')) return;
      const out = e.target.closest(".pdp__option")?.querySelector("output");
      if (out) out.textContent = e.target.value;
    });

    /* qty stepper -> hidden input */
    const qty = form.querySelector(".js-qty");
    const qtyInput = form.querySelector(".js-qty-input");
    if (qty && qtyInput) {
      const out = qty.querySelector("output");
      qty.querySelector("[data-plus]").addEventListener("click", () => { out.textContent = +out.textContent + 1; qtyInput.value = out.textContent; });
      qty.querySelector("[data-minus]").addEventListener("click", () => { out.textContent = Math.max(1, +out.textContent - 1); qtyInput.value = out.textContent; });
    }
  });

  /* ══════════ Product cards: gallery scroll + colour swatches ══════════ */
  const initProductCards = (root = document) => {
    root.querySelectorAll(".js-card-gallery").forEach((gallery) => {
      if (gallery.dataset.initialized === "true") return;
      gallery.dataset.initialized = "true";
      const track = gallery.querySelector(".product-card__gallery-track");
      const dots = [...gallery.querySelectorAll(".product-card__gallery-dot")];
      if (!track) return;

      let dragStart = 0;
      track.addEventListener("pointerdown", (e) => { dragStart = e.clientX; track.dataset.wasDragged = "false"; });
      track.addEventListener("pointermove", (e) => {
        if (Math.abs(e.clientX - dragStart) > 6) track.dataset.wasDragged = "true";
      });

      const syncDots = () => {
        if (!dots.length) return;
        const idx = Math.round(track.scrollLeft / track.clientWidth);
        dots.forEach((dot, i) => dot.classList.toggle("is-active", i === idx));
      };

      track.addEventListener("scroll", () => requestAnimationFrame(syncDots), { passive: true });
      syncDots();
    });

    bindVerticalWheelPassthrough(root);

    root.querySelectorAll(".js-product-card").forEach((card) => {
      if (card.dataset.cardInit === "true") return;
      card.dataset.cardInit = "true";

      card.addEventListener("click", (e) => {
        if (e.target.closest(".js-quickview, .product-card__swatch")) return;
        const track = e.target.closest(".product-card__gallery-track");
        if (track?.dataset.wasDragged === "true") {
          track.dataset.wasDragged = "false";
          return;
        }
        const url = card.dataset.url;
        if (url) window.location.href = url;
      });

      const variantsEl = card.querySelector(".js-card-variants");
      if (!variantsEl) return;
      let variants = [];
      try { variants = JSON.parse(variantsEl.textContent); } catch (err) { return; }

      const priceEl = card.querySelector(".js-card-price");
      const saleEl = card.querySelector(".product-card__sale");
      const firstImg = card.querySelector(".product-card__slide img");

      card.querySelectorAll(".js-card-swatches .product-card__swatch").forEach((btn) => {
        btn.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          const color = btn.dataset.color;
          const match = variants.find((v) =>
            [v.option1, v.option2, v.option3].includes(color) && v.available
          ) || variants.find((v) => [v.option1, v.option2, v.option3].includes(color));

          card.querySelectorAll(".product-card__swatch").forEach((s) => {
            const active = s === btn;
            s.classList.toggle("is-active", active);
            s.setAttribute("aria-pressed", active);
          });

          if (match) {
            if (priceEl) priceEl.textContent = moneyFmt(match.price);
            if (saleEl) {
              const pct = match.compare_at_price > match.price
                ? Math.round(((match.compare_at_price - match.price) / match.compare_at_price) * 100)
                : 0;
              if (pct > 0) saleEl.textContent = `-${pct}% ${moneyFmt(match.price)}`;
            }
            if (match.featured_image?.src && firstImg) {
              firstImg.src = match.featured_image.src.replace(/(\.[a-z]{3,4})(\?|$)/, "_800x$1$2");
            }
          }
        });
      });
    });
  };

  /* ══════════ Grid spacing toggle ══════════ */
  const applyGridSpacing = (mode) => {
    document.body.classList.remove("grid-spacing--flush");
    document.body.classList.add("grid-spacing--gapped");
    document.body.dataset.gridSpacing = "gapped";
  };

  const initGridSpacing = () => {
    applyGridSpacing("gapped");
  };

  /* ══════════ Customize panel ══════════ */
  const detectProfileProduct = (p) => {
    const isLuxe = (p.collections || []).some((c) => (c.title || c.handle || "").toLowerCase().includes("luxe")) ||
                   (p.tags || []).some((t) => (t || "").toLowerCase().includes("luxe")) ||
                   (p.title || "").toLowerCase().includes("luxe") ||
                   (p.type || "").toLowerCase().includes("luxe") ||
                   (p.vendor || "").toLowerCase().includes("luxe");
    const isClassic = (p.collections || []).some((c) => (c.title || c.handle || "").toLowerCase().includes("classic")) ||
                      (p.tags || []).some((t) => (t || "").toLowerCase().includes("classic")) ||
                      (p.title || "").toLowerCase().includes("classic") ||
                      (p.type || "").toLowerCase().includes("classic") ||
                      (p.vendor || "").toLowerCase().includes("classic");

    if (isLuxe && !isClassic) return "none";

    const t = ((p.type || "") + " " + (p.title || "")).toLowerCase();
    if (t.includes("trouser") || t.includes("pant") || t.includes("jogger")) return "pants";
    if (isClassic || t.includes("shirt") || t.includes("top") || t.includes("kurta") || t.includes("pajama") || t.length > 0) return "shirt";
    return "none";
  };

  const openCustomize = (profile, formId) => {
    const panel = document.querySelector(".js-customize-panel");
    const cOverlay = document.querySelector(".js-customize-overlay");
    if (!panel || profile === "none") return;
    panel.querySelectorAll("[data-profile-content]").forEach((el) => {
      el.hidden = el.dataset.profileContent !== profile;
    });
    panel.dataset.activeForm = formId;
    panel.querySelectorAll('[form="pdpProductForm"]').forEach((el) => el.setAttribute("form", formId));
    if (openDialog && openDialog !== panel) dialogUnderCustomize = openDialog;
    else dialogUnderCustomize = document.querySelector(".js-quickview-modal.is-open");
    panel.hidden = false;
    if (cOverlay) cOverlay.hidden = false;
    void panel.offsetWidth;
    panel.classList.add("is-open");
    cOverlay?.classList.add("is-open");
    document.body.classList.add("customize-open");
    openDialog = panel;
    panel.focus({ preventScroll: true });
  };

  const closeCustomize = () => {
    const panel = document.querySelector(".js-customize-panel");
    const cOverlay = document.querySelector(".js-customize-overlay");
    if (!panel?.classList.contains("is-open")) return;
    panel.classList.remove("is-open");
    cOverlay?.classList.remove("is-open");
    setTimeout(() => {
      if (!panel.classList.contains("is-open")) {
        panel.hidden = true;
        if (cOverlay) cOverlay.hidden = true;
        document.body.classList.remove("customize-open");
        if (openDialog === panel) {
          const parent = dialogUnderCustomize?.classList?.contains("is-open")
            ? dialogUnderCustomize
            : document.querySelector(".js-quickview-modal.is-open, .modal.is-open, .drawer.is-open, .popup.is-open");
          openDialog = parent && !parent.classList.contains("js-customize-panel") ? parent : null;
          dialogUnderCustomize = null;
        }
      }
    }, 460);
  };

  const initCustomizePanel = () => {
    // Dynamic output updates in customize panel
    document.querySelector(".js-customize-panel")?.addEventListener("change", (e) => {
      const block = e.target.closest(".customize-block");
      if (!block) return;
      const output = block.querySelector(".js-customize-output");
      if (!output) return;

      const checkedRadios = block.querySelectorAll('input[type="radio"]:checked');
      if (checkedRadios.length === 1) {
        output.textContent = checkedRadios[0].value;
      } else if (checkedRadios.length > 1) {
        output.textContent = Array.from(checkedRadios).map((r) => r.value).join(" · ");
      }
    });

    // Dynamic Monogram label update
    const updateMonogramLabel = () => {
      const input = document.querySelector(".js-monogram-input");
      const label = document.querySelector(".js-monogram-label");
      if (!input || !label) return;
      const val = input.value.trim().toUpperCase();
      const style = document.querySelector('input[name="properties[Monogram style]"]:checked')?.value || "Bold";
      label.textContent = val ? `${val} (${style})` : "Optional";
    };

    // Uppercase monogram input and update label
    document.addEventListener("input", (e) => {
      if (e.target.matches(".js-monogram-input")) {
        e.target.value = e.target.value.toUpperCase();
        updateMonogramLabel();
      }
    });

    document.addEventListener("change", (e) => {
      if (e.target.name === "properties[Monogram style]") {
        updateMonogramLabel();
      }
    });

    document.addEventListener("click", (e) => {
      const opener = e.target.closest(".js-customize-open");
      if (opener) {
        e.preventDefault();
        e.stopPropagation();
        const profile = opener.dataset.profile || document.querySelector(".js-pdp")?.dataset.profile || "shirt";
        openCustomize(profile, opener.dataset.form || "pdpProductForm");
        return;
      }
      if (e.target.closest(".js-customize-close") || e.target.classList.contains("js-customize-overlay")) {
        closeCustomize();
        return;
      }
      const atc = e.target.closest(".js-customize-atc");
      if (!atc) return;
      e.preventDefault();
      const panel = document.querySelector(".js-customize-panel");
      const form = document.getElementById(panel?.dataset.activeForm);
      if (!form) return;
      const fd = new FormData(form);
      panel.querySelectorAll('input[name^="properties"], textarea[name^="properties"]').forEach((el) => {
        if (el.type === "radio" && !el.checked) return;
        if (el.value) fd.set(el.name, el.value);
      });
      const monoVal = (fd.get("properties[Monogram]") || "").toString().trim();
      if (!monoVal) {
        fd.delete("properties[Monogram]");
        fd.delete("properties[Monogram style]");
        fd.delete("properties[Monogram placement]");
      }
      atc.disabled = true;
      fetch("/cart/add.js", { method: "POST", body: fd })
        .then((r) => r.json())
        .then((res) => {
          if (res.status && res.status !== 200) throw new Error(res.description || "Could not add to cart");
          closeCustomize();
          return refreshCart();
        })
        .then(() => openDialogEl(document.getElementById("cartDrawer"), atc))
        .catch((err) => alert(err.message))
        .finally(() => { atc.disabled = false; });
    });
  };

  /* ── PDP Gallery Height Sync ─────────────────────────────────────
   * Makes the left gallery-wrap exactly as tall as the right buybox
   * so the sticky image panel never leaves blank white space.         */
  const syncGalleryHeight = () => {
    if (window.innerWidth <= 900) return;
    const galleryWrap = document.querySelector(".pdp__gallery-wrap");
    const buybox      = document.querySelector(".pdp__buybox");
    if (!galleryWrap || !buybox) return;
    // Reset first so we measure the buybox's true natural height
    galleryWrap.style.minHeight = "";
    const buyboxH = buybox.getBoundingClientRect().height;
    galleryWrap.style.minHeight = buyboxH + "px";
  };

  const initPdpRhone = () => {

    const gallery = document.querySelector(".js-pdp-gallery");
    document.querySelector(".js-pdp-gallery-more")?.addEventListener("click", () => {
      gallery?.classList.add("is-expanded");
      document.querySelector(".js-pdp-gallery-more")?.remove();
    });

    const track = document.querySelector(".js-pdp-mobile-track");
    if (track) {
      const step = () => track.clientWidth;
      document.querySelector(".js-pdp-prev")?.addEventListener("click", () => {
        track.scrollBy({ left: -step(), behavior: reduceMotion ? "auto" : "smooth" });
      });
      document.querySelector(".js-pdp-next")?.addEventListener("click", () => {
        track.scrollBy({ left: step(), behavior: reduceMotion ? "auto" : "smooth" });
      });
    }

    // ── Stacked Vertical Images & Thumbnails Sync ──
    const thumbs = document.querySelectorAll(".js-pdp-thumb-btn");
    const slides = document.querySelectorAll(".js-pdp-main-slide");
    const mainWrap = document.querySelector(".js-pdp-main-image-wrap");
    
    if (thumbs.length > 0 && slides.length > 0) {
      thumbs.forEach((thumb) => {
        thumb.addEventListener("click", () => {
          const idx = thumb.getAttribute("data-index");
          thumbs.forEach((t) => t.classList.remove("is-active"));
          thumb.classList.add("is-active");
          
          const targetSlide = document.querySelector(`.js-pdp-main-slide[data-index="${idx}"]`);
          if (targetSlide && mainWrap) {
            mainWrap.scrollTo({ top: targetSlide.offsetTop, behavior: reduceMotion ? "auto" : "smooth" });
          } else if (targetSlide) {
            targetSlide.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "center" });
          }
        });
      });

      // Highlight active thumbnail as user scrolls inside mainWrap
      if ("IntersectionObserver" in window && window.innerWidth > 900) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const idx = entry.target.getAttribute("data-index");
              thumbs.forEach((t) => t.classList.remove("is-active"));
              const activeThumb = document.querySelector(`.js-pdp-thumb-btn[data-index="${idx}"]`);
              if (activeThumb) activeThumb.classList.add("is-active");
            }
          });
        }, { root: mainWrap || null, threshold: 0.5 });
        slides.forEach((slide) => observer.observe(slide));
      }
    }

    // ── Size selection label update ──
    const sizeInputs = document.querySelectorAll(".pdp__option--size input[type='radio']");
    const sizeOutput = document.querySelector(".js-size-label");
    if (sizeInputs.length > 0 && sizeOutput) {
      sizeInputs.forEach(input => {
        input.addEventListener("change", () => {
          sizeOutput.textContent = input.value;
        });
      });
    }

    const accordion = document.querySelector(".js-pdp-accordion");
    if (accordion) {
      const OPEN_OVERLAP_MS = 280;
      const CLOSE_RELEASE_MS = 420;
      const SWITCH_CLEANUP_MS = 1100;
      const CLOSE_CLEANUP_MS = 900;

      const finishAccordionMotion = () => {
        accordion.querySelectorAll(".is-acc-closing, .is-acc-opening").forEach((item) => {
          item.classList.remove("is-acc-closing", "is-acc-opening");
        });
        accordion.classList.remove("is-switching");
        accordion.dataset.switching = "false";
      };

      accordion.querySelectorAll(".pdp__acc-item summary").forEach((summary) => {
        summary.addEventListener("click", (e) => {
          const target = summary.closest(".pdp__acc-item");
          const current = accordion.querySelector(".pdp__acc-item[open]");
          if (!target) return;

          if (target === current) {
            e.preventDefault();
            if (accordion.dataset.switching === "true") return;

            accordion.dataset.switching = "true";
            accordion.classList.add("is-switching");
            current.classList.add("is-acc-closing");

            if (reduceMotion) {
              current.open = false;
              finishAccordionMotion();
              return;
            }

            window.setTimeout(() => {
              current.open = false;
            }, CLOSE_RELEASE_MS);

            window.setTimeout(finishAccordionMotion, CLOSE_CLEANUP_MS);
            return;
          }

          if (!current) return;

          e.preventDefault();
          if (accordion.dataset.switching === "true") return;
          accordion.dataset.switching = "true";
          accordion.classList.add("is-switching");

          if (reduceMotion) {
            current.open = false;
            target.open = true;
            finishAccordionMotion();
            return;
          }

          current.classList.add("is-acc-closing");

          window.setTimeout(() => {
            current.open = false;
            target.classList.add("is-acc-opening");
            target.open = true;
          }, OPEN_OVERLAP_MS);

          window.setTimeout(finishAccordionMotion, SWITCH_CLEANUP_MS);
          // Re-measure after accordion animation settles
          window.setTimeout(syncGalleryHeight, SWITCH_CLEANUP_MS + 50);
        });
      });
    }

    // Initial sync + on resize
    syncGalleryHeight();
    window.addEventListener("resize", syncGalleryHeight);
  };

  const initSizeChart = () => {
    const modal = document.querySelector(".js-sizechart-modal");
    if (!modal) return;

    const tops = modal.querySelector(".js-sizechart-tops");
    const bottoms = modal.querySelector(".js-sizechart-bottoms");
    const toggle = modal.querySelector(".sizechart__toggle");
    const labels = [...modal.querySelectorAll(".sizechart__unit-label")];

    const cacheInch = (table) => {
      if (!table || table.dataset.cached === "true") return;
      table.querySelectorAll("tbody td").forEach((cell) => {
        const n = parseFloat(cell.textContent);
        if (!Number.isNaN(n) && cell.cellIndex >= 2) cell.dataset.inch = String(n);
      });
      table.dataset.cached = "true";
    };

    const setUnit = (unit) => {
      const toCm = unit === "cm";
      [tops, bottoms].forEach((table) => {
        if (!table) return;
        cacheInch(table);
        table.querySelectorAll("tbody td").forEach((cell) => {
          if (!cell.dataset.inch) return;
          const inch = parseFloat(cell.dataset.inch);
          cell.textContent = toCm ? (inch * 2.54).toFixed(1) : String(inch);
        });
        table.dataset.unit = unit;
      });
      toggle?.setAttribute("aria-pressed", toCm);
      labels.forEach((l) => l.classList.toggle("is-active", l.dataset.unit === unit));
    };

    toggle?.addEventListener("click", () => {
      setUnit(tops?.dataset.unit === "cm" ? "inch" : "cm");
    });

    document.addEventListener("click", (e) => {
      if (!e.target.closest(".js-sizechart-open")) return;
      e.preventDefault();
      e.stopPropagation();
      setUnit("inch");
      modal.hidden = false;
      const customizeOpen = document.querySelector(".js-customize-panel")?.classList.contains("is-open");
      if (!customizeOpen) {
        if (overlay) overlay.hidden = false;
        overlay?.classList.add("is-open");
        document.body.classList.add("drawer-open");
      }
      void modal.offsetWidth;
      modal.classList.add("is-open");
      openDialog = modal;
      modal.focus({ preventScroll: true });
    });
  };

  /* ══════════ Quick view (native product JSON) ══════════ */
  const esc = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/"/g, "&quot;");

  const parseProductOptions = (p) => {
    const names = Array.isArray(p.options) ? p.options : [];
    return names.map((opt, i) => {
      const name = typeof opt === "string" ? opt : (opt.name || "");
      const key = `option${i + 1}`;
      const values = [...new Set(p.variants.map((v) => v[key]).filter((v) => v != null && v !== ""))];
      return { name, position: i + 1, key, values };
    });
  };

  const variantImageSrc = (variant, fallback) => {
    const fi = variant?.featured_image;
    if (typeof fi === "string") return fi;
    if (fi?.src) return fi.src;
    return fallback;
  };

  const buildQuickView = (p, body, profileOverride) => {
    const images = (p.images?.length ? p.images : (p.featured_image ? [p.featured_image] : [])).map((img) =>
      typeof img === "string" ? img : img.src
    );
    const options = parseProductOptions(p);
    const colorOpt = options.find((o) => /colou?r/i.test(o.name));
    const sizeOpt = options.find((o) => /size/i.test(o.name));
    const otherOpts = options.filter((o) => o !== colorOpt && o !== sizeOpt);
    const orderedOpts = [colorOpt, sizeOpt, ...otherOpts].filter(Boolean);

    const firstVariant = p.variants.find((v) => v.available) || p.variants[0];
    const multiImages = images.length > 1;

    const thumbs = multiImages
      ? images.map((src, i) =>
          `<button type="button" class="quickview__thumb${i === 0 ? " is-active" : ""}" data-idx="${i}">
            <img src="${src.replace(/(\.[a-z]{3,4})(\?|$)/, "_200x$1$2")}" alt="">
          </button>`
        ).join("")
      : "";

    const mainSrc = images[0] ? images[0].replace(/(\.[a-z]{3,4})(\?|$)/, "_900x$1$2") : "";

    const renderOption = (opt) => {
      const isColor = /colou?r/i.test(opt.name);
      const isSize = /size/i.test(opt.name);
      const wrapClass = isColor ? "quickview__swatches js-qv-colors" : `quickview__sizes js-qv-sizes${isSize ? "" : " js-qv-opt"}`;
      const btnClass = isColor ? "quickview__swatch" : "quickview__size";
      const labelRow = isSize
        ? `<div class="quickview__label-row">
             <p class="quickview__label">Size</p>
             <button type="button" class="quickview__sizechart js-sizechart-open">Size chart</button>
           </div>`
        : `<p class="quickview__label">${esc(isColor ? "Color" : opt.name)}</p>`;

      const selectedVal = firstVariant?.[opt.key];
      return `
        ${labelRow}
        <div class="${wrapClass}" data-name="${esc(opt.name)}">
          ${opt.values.map((v) =>
            `<button type="button" class="${btnClass}${v === selectedVal ? " is-active" : ""}" data-value="${esc(v)}">${esc(v)}</button>`
          ).join("")}
        </div>`;
    };

    const optionsHtml = orderedOpts.map(renderOption).join("");
    const qvProfile = profileOverride || detectProfileProduct(p);
    const customizeBtn = qvProfile !== "none"
      ? `<button type="button" class="button button--ghost button--block js-customize-open" data-form="qvProductForm" data-profile="${qvProfile}">Customize</button>`
      : "";

    const lowStock = firstVariant?.inventory_quantity > 0 && firstVariant.inventory_quantity <= 3;
    const priceHtml = firstVariant?.compare_at_price > firstVariant?.price
      ? `<span class="quickview__compare">${moneyFmt(firstVariant.compare_at_price)}</span> ${moneyFmt(firstVariant.price)}`
      : moneyFmt(firstVariant?.price ?? p.price);

    body.innerHTML = `
      <div class="quickview">
        <div class="quickview__gallery${multiImages ? "" : " quickview__gallery--single"}">
          ${multiImages ? `<div class="quickview__thumbs">${thumbs}</div>` : ""}
          <div class="quickview__main"><img class="js-qv-main" src="${mainSrc}" alt="${esc(p.title)}"></div>
        </div>
        <div class="quickview__info">
          <h3 class="quickview__title">${esc(p.title.toLowerCase())}</h3>
          <p class="quickview__price js-qv-price">${priceHtml} <span>Tax included.</span></p>
          <form id="qvProductForm" class="js-qv-form" method="post" action="/cart/add">
            <input type="hidden" name="id" value="${firstVariant?.id || p.variants[0]?.id}" class="js-qv-variant-id">
            ${optionsHtml}
            <p class="quickview__stock${lowStock ? " is-low" : ""} js-qv-stock">${
              lowStock
                ? `Low stock — ${firstVariant.inventory_quantity} left`
                : (firstVariant?.available ? "In stock" : "Sold out")
            }</p>
            <p class="quickview__label">Quantity</p>
            <span class="qty js-qv-qty">
              <button type="button" data-minus aria-label="Decrease quantity">−</button>
              <output aria-label="Quantity">1</output>
              <button type="button" data-plus aria-label="Increase quantity">＋</button>
            </span>
            <input type="hidden" name="quantity" value="1" class="js-qv-qty-input">
            <div class="quickview__actions">
              <button class="button button--block js-qv-atc" type="submit" ${p.available ? "" : "disabled"}>Add to basket</button>
              ${customizeBtn}
              <button class="button button--ghost button--block js-qv-buy" type="button" ${p.available ? "" : "disabled"}>Buy it now</button>
            </div>
          </form>
          <a class="quickview__link" href="${p.url}">View full details</a>
        </div>
      </div>`;

    const form = body.querySelector(".js-qv-form");
    const variantInput = form.querySelector(".js-qv-variant-id");
    const priceEl = body.querySelector(".js-qv-price");
    const mainImg = body.querySelector(".js-qv-main");
    const stockEl = body.querySelector(".js-qv-stock");
    const atcBtn = form.querySelector(".js-qv-atc");

    const chosen = {};
    options.forEach((o) => { chosen[o.name] = firstVariant?.[o.key] ?? o.values[0]; });

    const resolveVariant = () => {
      const match = p.variants.find((v) =>
        options.every((o) => v[o.key] === chosen[o.name])
      );
      if (!match) return;

      variantInput.value = match.id;
      const priceDisplay = match.compare_at_price > match.price
        ? `<span class="quickview__compare">${moneyFmt(match.compare_at_price)}</span> ${moneyFmt(match.price)}`
        : moneyFmt(match.price);
      priceEl.innerHTML = `${priceDisplay} <span>Tax included.</span>`;
      atcBtn.disabled = !match.available;
      atcBtn.textContent = match.available ? "Add to basket" : "Sold out";

      const imgSrc = variantImageSrc(match, images[0]);
      if (imgSrc && mainImg) {
        mainImg.src = imgSrc.replace(/(\.[a-z]{3,4})(\?|$)/, "_900x$1$2");
        body.querySelectorAll(".quickview__thumb").forEach((btn) => btn.classList.remove("is-active"));
      }

      if (match.inventory_quantity > 0 && match.inventory_quantity <= 3) {
        stockEl.className = "quickview__stock is-low js-qv-stock";
        stockEl.textContent = `Low stock — ${match.inventory_quantity} left`;
      } else {
        stockEl.className = "quickview__stock js-qv-stock";
        stockEl.textContent = match.available ? "In stock" : "Sold out";
      }

      if (sizeOpt) {
        body.querySelectorAll(".js-qv-sizes .quickview__size").forEach((btn) => {
          const test = { ...chosen, [sizeOpt.name]: btn.dataset.value };
          const ok = p.variants.some((v) =>
            options.every((o) => v[o.key] === test[o.name]) && v.available
          );
          btn.disabled = !ok;
        });
      }
    };

    body.querySelectorAll(".quickview__thumb").forEach((btn) => {
      btn.addEventListener("click", () => {
        body.querySelectorAll(".quickview__thumb").forEach((b) => b.classList.remove("is-active"));
        btn.classList.add("is-active");
        const src = images[+btn.dataset.idx];
        if (src && mainImg) mainImg.src = src.replace(/(\.[a-z]{3,4})(\?|$)/, "_900x$1$2");
      });
    });

    const bindOpt = (wrap) => {
      if (!wrap) return;
      const name = wrap.dataset.name;
      wrap.querySelectorAll("button").forEach((btn) => {
        btn.addEventListener("click", () => {
          wrap.querySelectorAll("button").forEach((b) => b.classList.remove("is-active"));
          btn.classList.add("is-active");
          chosen[name] = btn.dataset.value;
          resolveVariant();
        });
      });
    };

    body.querySelectorAll(".js-qv-colors, .js-qv-sizes, .js-qv-opt").forEach(bindOpt);

    const qty = form.querySelector(".js-qv-qty");
    const qtyInput = form.querySelector(".js-qv-qty-input");
    if (qty && qtyInput) {
      const out = qty.querySelector("output");
      qty.querySelector("[data-plus]").addEventListener("click", () => { out.textContent = +out.textContent + 1; qtyInput.value = out.textContent; });
      qty.querySelector("[data-minus]").addEventListener("click", () => { out.textContent = Math.max(1, +out.textContent - 1); qtyInput.value = out.textContent; });
    }

    form.querySelector(".js-qv-buy")?.addEventListener("click", () => {
      const fd = new FormData(form);
      fetch("/cart/add.js", { method: "POST", body: fd })
        .then(() => { window.location.href = "/checkout"; });
    });

    resolveVariant();
  };

  document.addEventListener("click", (e) => {
    const qv = e.target.closest(".js-quickview");
    if (!qv) return;
    e.preventDefault();
    e.stopPropagation();
    fetch(`/products/${qv.dataset.handle}.js`)
      .then((r) => r.json())
      .then((p) => {
        const body = document.querySelector(".js-quickview-body");
        buildQuickView(p, body, qv.dataset.profile);
        openDialogEl(document.querySelector(".js-quickview-modal"), qv);
      });
  });

  /* ══════════ Zoom modal ══════════ */
  const openProductZoom = (z) => {
    const img = document.querySelector(".js-zoom-img");
    const preview = z.querySelector("img");
    const src = z.dataset.zoomSrc || preview?.currentSrc || preview?.src;
    if (!src) return;
    img.classList.remove("is-zoomed");
    img.src = src;
    img.alt = preview?.alt || "Zoomed product image";
    openDialogEl(document.querySelector(".js-zoom-modal"), z);
  };

  document.addEventListener("click", (e) => {
    const z = e.target.closest(".js-zoomable");
    if (!z) return;
    openProductZoom(z);
  });

  document.addEventListener("keydown", (e) => {
    const z = e.target.closest(".js-zoomable");
    if (!z || (e.key !== "Enter" && e.key !== " ")) return;
    e.preventDefault();
    openProductZoom(z);
  });

  document.querySelector(".js-zoom-img")?.addEventListener("click", (e) => {
    e.currentTarget.classList.toggle("is-zoomed");
  });

  /* ══════════ Predictive search ══════════ */
  let searchTimer;
  document.addEventListener("input", (e) => {
    if (!e.target.matches('.search-drawer__form input[name="q"]')) return;
    const q = e.target.value.trim();
    const results = document.querySelector(".js-search-results");
    const idle = document.querySelectorAll(".search-drawer__trending");
    if (!results) return;
    clearTimeout(searchTimer);
    if (!q) {
      results.innerHTML = "";
      idle.forEach((el) => (el.hidden = false));
      return;
    }
    searchTimer = setTimeout(() => {
      fetch(`/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=5`)
        .then((r) => r.json())
        .then((data) => {
          idle.forEach((el) => (el.hidden = true));
          const products = data.resources.results.products || [];
          results.innerHTML = products.length
            ? `<p class="eyebrow">Products</p>` + products.map((p) => `
              <a class="search-result" href="${p.url}">
                ${p.featured_image?.url ? `<img src="${p.featured_image.url.replace(/(\.[a-z]{3,4})(\?|$)/, "_200x$1$2")}" alt="" loading="lazy">` : ""}
                <span><strong>${p.title}</strong><span>${p.vendor || ""}</span></span>
                <span class="search-result__price">${p.price ? "₹" + parseFloat(p.price).toLocaleString("en-IN") : ""}</span>
              </a>`).join("")
            : `<p class="search-result__none">Nothing found for “${q}”. Try “oxford” or “trouser” — or <a href="/#bespoke">go bespoke</a>.</p>`;
        });
    }, 220);
  });

  /* ══════════ Collection: AJAX filters, sort, pagination ══════════ */
  let collectionAbort;

  const collectionUrlFromForm = (form) => {
    const url = new URL(window.location.href);
    const formData = new FormData(form);
    url.search = "";
    formData.forEach((value, key) => {
      if (value !== "") url.searchParams.append(key, value);
    });
    const sort = document.querySelector(".js-sort-select")?.value;
    if (sort) url.searchParams.set("sort_by", sort);
    url.searchParams.delete("page");
    return url;
  };

  const updateCollection = (url, pushState = true) => {
    const layout = document.querySelector(".js-collection-layout");
    if (!layout) return;

    const sectionId = layout.dataset.sectionId;
    const requestUrl = new URL(url.toString());
    requestUrl.searchParams.set("section_id", sectionId);
    const filtersCollapsed = layout.classList.contains("is-filters-collapsed");
    const filtersOpen = layout.classList.contains("is-filters-open");
    const filtersTouched = layout.dataset.filtersTouched;

    // Collect titles of all currently open accordion filter groups
    const openGroupTitles = Array.from(layout.querySelectorAll(".filters__group.is-open .filters__toggle"))
      .map((btn) => btn.textContent.trim().toUpperCase());

    // Collect all active checked filter parameters from target URL
    const urlParams = Array.from(requestUrl.searchParams.entries());

    collectionAbort?.abort();
    collectionAbort = new AbortController();
    layout.classList.add("is-loading");

    fetch(requestUrl.toString(), { signal: collectionAbort.signal })
      .then((response) => response.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, "text/html");
        const nextLayout = doc.querySelector(".js-collection-layout");
        if (!nextLayout) return;

        if (filtersCollapsed) nextLayout.classList.add("is-filters-collapsed");
        if (filtersOpen) nextLayout.classList.add("is-filters-open");
        if (filtersTouched) nextLayout.dataset.filtersTouched = filtersTouched;

        // Restore accordion open states in nextLayout
        nextLayout.querySelectorAll(".filters__group").forEach((group) => {
          const btn = group.querySelector(".filters__toggle");
          if (!btn) return;
          const title = btn.textContent.trim().toUpperCase();
          if (openGroupTitles.includes(title)) {
            group.classList.add("is-open");
            btn.setAttribute("aria-expanded", "true");
          }
        });

        // Restore checked attribute on inputs whose name & value match active URL searchParams
        nextLayout.querySelectorAll('.js-filter-form input[type="checkbox"]').forEach((input) => {
          const isMatch = urlParams.some(([paramName, paramVal]) => paramName === input.name && paramVal === input.value);
          if (isMatch) {
            input.checked = true;
          }
        });

        layout.replaceWith(nextLayout);
        if (pushState) window.history.pushState({}, "", url.toString());
        initCollection(nextLayout);
        observeReveals(nextLayout);
        initProductCards(nextLayout);
      })
      .catch((error) => {
        if (error.name !== "AbortError") window.location.href = url.toString();
      });
  };

  const initPriceRange = (root = document) => {
    root.querySelectorAll(".js-price-range").forEach((wrap) => {
      if (wrap.dataset.initialized === "true") return;
      wrap.dataset.initialized = "true";

      const minRange = wrap.querySelector(".js-price-range-min");
      const maxRange = wrap.querySelector(".js-price-range-max");
      const minInput = wrap.querySelector(".js-price-range-min-input");
      const maxInput = wrap.querySelector(".js-price-range-max-input");
      const minLabel = wrap.querySelector(".js-price-range-min-label");
      const maxLabel = wrap.querySelector(".js-price-range-max-label");
      const fill = wrap.querySelector(".js-price-range-fill");
      const form = wrap.closest("form");
      const rangeMin = Number(wrap.dataset.min);
      const rangeMax = Number(wrap.dataset.max);

      const format = (v) => "₹" + Number(v).toLocaleString("en-IN");

      const update = () => {
        let min = Number(minRange.value);
        let max = Number(maxRange.value);
        if (min > max) {
          if (document.activeElement === minRange) {
            max = min;
            maxRange.value = String(max);
          } else {
            min = max;
            minRange.value = String(min);
          }
        }
        minLabel.textContent = format(min);
        maxLabel.textContent = format(max);
        const span = rangeMax - rangeMin || 1;
        const pctMin = ((min - rangeMin) / span) * 100;
        const pctMax = ((max - rangeMin) / span) * 100;
        fill.style.left = pctMin + "%";
        fill.style.width = (pctMax - pctMin) + "%";

        if (min <= rangeMin) {
          minInput.disabled = true;
          minInput.value = "";
        } else {
          minInput.disabled = false;
          minInput.value = String(min);
        }
        if (max >= rangeMax) {
          maxInput.disabled = true;
          maxInput.value = "";
        } else {
          maxInput.disabled = false;
          maxInput.value = String(max);
        }
      };

      const submitRange = () => updateCollection(collectionUrlFromForm(form));

      minRange.addEventListener("input", update);
      maxRange.addEventListener("input", update);
      minRange.addEventListener("change", submitRange);
      maxRange.addEventListener("change", submitRange);
      update();
    });
  };

  const initFilterSidebar = (layout) => {
    if (!layout || layout.dataset.filterSidebarInit === "true") return;
    layout.dataset.filterSidebarInit = "true";

    const toggle = layout.querySelector(".js-filter-sidebar-toggle");
    const backdrop = layout.querySelector(".js-filter-backdrop");
    const mq = window.matchMedia("(max-width: 900px)");

    const isOpen = () => (
      mq.matches
        ? layout.classList.contains("is-filters-open")
        : !layout.classList.contains("is-filters-collapsed")
    );

    const syncUi = () => {
      const open = isOpen();
      toggle?.setAttribute("aria-expanded", String(open));
      toggle?.classList.toggle("is-active", open);
      if (backdrop) backdrop.hidden = !(mq.matches && open);
    };

    const setOpen = (open) => {
      if (mq.matches) {
        layout.classList.toggle("is-filters-open", open);
        layout.classList.toggle("is-filters-collapsed", !open);
      } else {
        layout.classList.toggle("is-filters-collapsed", !open);
        layout.classList.remove("is-filters-open");
      }
      syncUi();
    };

    if (!layout.dataset.filtersTouched) {
      if (mq.matches) {
        setOpen(false);
      } else {
        setOpen(true);
      }
    } else {
      syncUi();
    }

    toggle?.addEventListener("click", () => {
      layout.dataset.filtersTouched = "true";
      setOpen(!isOpen());
    });

    backdrop?.addEventListener("click", () => {
      layout.dataset.filtersTouched = "true";
      setOpen(false);
    });

    mq.addEventListener("change", () => {
      if (mq.matches) {
        const open = !layout.classList.contains("is-filters-collapsed");
        layout.classList.toggle("is-filters-open", open);
      } else {
        layout.classList.remove("is-filters-open");
        if (backdrop) backdrop.hidden = true;
      }
      syncUi();
    });
  };



  const initCustomDropdowns = (root = document) => {
    root.querySelectorAll(".js-custom-dropdown").forEach((dropdown) => {
      const btn = dropdown.querySelector(".js-dropdown-btn");
      const list = dropdown.querySelector(".js-dropdown-list");
      const hiddenSelect = dropdown.parentElement.querySelector(".js-sort-select");

      if (!btn || !list || !hiddenSelect) return;

      const closeDropdown = () => {
        dropdown.classList.remove("is-open");
        list.hidden = true;
        btn.setAttribute("aria-expanded", "false");
      };

      const openDropdown = () => {
        dropdown.classList.add("is-open");
        list.hidden = false;
        btn.setAttribute("aria-expanded", "true");
      };

      btn.addEventListener("click", (e) => {
        e.stopPropagation();
        if (list.hidden) {
          document.querySelectorAll(".js-custom-dropdown").forEach(d => {
            if (d !== dropdown) {
              d.classList.remove("is-open");
              const otherList = d.querySelector(".js-dropdown-list");
              const otherBtn = d.querySelector(".js-dropdown-btn");
              if (otherList) otherList.hidden = true;
              if (otherBtn) otherBtn.setAttribute("aria-expanded", "false");
            }
          });
          openDropdown();
        } else {
          closeDropdown();
        }
      });

      dropdown.querySelectorAll(".js-dropdown-item").forEach((item) => {
        item.addEventListener("click", () => {
          const val = item.dataset.value;
          const labelText = item.textContent.trim();
          const btnSpan = btn.querySelector("span");
          if (btnSpan) btnSpan.textContent = "Sort By: " + labelText;

          dropdown.querySelectorAll(".js-dropdown-item").forEach(i => i.classList.remove("is-selected"));
          item.classList.add("is-selected");

          hiddenSelect.value = val;
          hiddenSelect.dispatchEvent(new Event("change"));
          closeDropdown();
        });
      });

      document.addEventListener("click", (e) => {
        if (!dropdown.contains(e.target)) {
          closeDropdown();
        }
      });
    });
  };

  const initCategorySquares = (root = document) => {
    const squares = root.querySelectorAll(".js-category-square");
    if (!squares.length) return;

    const params = new URLSearchParams(window.location.search);
    const activeType = params.get("filter.p.product_type");

    squares.forEach((sq) => {
      const type = sq.dataset.type;
      if (type === activeType) {
        sq.classList.add("is-active");
      } else {
        sq.classList.remove("is-active");
      }

      sq.addEventListener("click", () => {
        const url = new URL(window.location.href);
        if (type === activeType) {
          url.searchParams.delete("filter.p.product_type");
        } else {
          url.searchParams.set("filter.p.product_type", type);
        }
        url.searchParams.delete("page");
        updateCollection(url);
      });
    });
  };

  const initCollection = (root = document) => {
    initCustomDropdowns(root);
    initCategorySquares(root);

    const layout = root.classList?.contains("js-collection-layout")
      ? root
      : root.querySelector?.(".js-collection-layout");
    if (layout) initFilterSidebar(layout);

    root.querySelectorAll(".js-sort-select, .js-sort-by").forEach((select) => {
      select.addEventListener("change", (e) => {
        const form = document.querySelector(".js-filter-form");
        const url = form ? collectionUrlFromForm(form) : new URL(window.location.href);
        url.searchParams.set("sort_by", e.target.value);
        url.searchParams.delete("page");
        updateCollection(url);
      });
    });

    root.querySelector(".js-filter-form")?.addEventListener("change", (e) => {
      if (e.target.closest(".js-price-range")) return;
      updateCollection(collectionUrlFromForm(e.currentTarget));
    });

    initPriceRange(root);

    root.querySelectorAll(".js-grid-switch button").forEach((btn) => {
      btn.addEventListener("click", () => {
        const grid = document.querySelector(".js-collection-grid");
        if (!grid) return;
        grid.classList.remove("product-grid--3", "product-grid--4");
        grid.classList.add("product-grid--" + btn.dataset.cols);
        btn.parentElement.querySelectorAll("button").forEach((b) => {
          b.classList.toggle("is-active", b === btn);
          b.setAttribute("aria-pressed", b === btn);
        });
      });
    });

    root.querySelectorAll(".filters__toggle").forEach((btn) => {
      btn.addEventListener("click", () => {
        const open = btn.closest(".filters__group").classList.toggle("is-open");
        btn.setAttribute("aria-expanded", open);
      });
    });

    root.querySelectorAll(".js-collection-pagination a").forEach((link) => {
      link.addEventListener("click", (e) => {
        e.preventDefault();
        updateCollection(new URL(link.href));
      });
    });
  };

  window.addEventListener("popstate", () => updateCollection(new URL(window.location.href), false));

  /* ══════════ Recommendations (section rendering) ══════════ */
  document.querySelectorAll("[data-recommendations]").forEach((el) => {
    if (el.children.length) return;
    fetch(el.dataset.url)
      .then((r) => r.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, "text/html");
        const inner = doc.querySelector("[data-recommendations]");
        if (inner && inner.children.length) {
          el.innerHTML = inner.innerHTML;
          observeReveals(el.parentElement);
          initProductCards(el);
        }
      });
  });

  /* ══════════ Hotspots ══════════ */
  document.querySelectorAll(".js-hotspot").forEach((spot) => {
    spot.addEventListener("click", () => {
      const wrap = spot.closest(".js-look");
      const wasActive = spot.classList.contains("is-active");
      wrap.querySelectorAll(".js-hotspot").forEach((s) => { s.classList.remove("is-active"); s.setAttribute("aria-expanded", "false"); });
      wrap.querySelectorAll(".look-card").forEach((c) => c.classList.remove("is-active"));
      if (!wasActive) {
        spot.classList.add("is-active");
        spot.setAttribute("aria-expanded", "true");
        wrap.querySelector(spot.dataset.target)?.classList.add("is-active");
      }
    });
  });


  /* ══════════ Inner Circle Popup Engine ══════════════════════════
   *
   * State keys (localStorage):
   *   ic-subscribed      → "1"  : visitor signed up — never show again
   *   ic-welcome-shown   → timestamp : when welcome was last shown
   *   ic-exit-shown      → "1"  : exit popup already shown this session
   *
   * Rules:
   *  • Only one popup visible at a time.
   *  • If subscribed → show neither popup.
   *  • Welcome: show once; if dismissed, re-show only after 45 min.
   *  • Exit: show once per session; skip if subscribed.
   * ══════════════════════════════════════════════════════════════ */
  (() => {
    const LS_SUB      = "ic-subscribed";
    const LS_WELCOME  = "ic-welcome-shown";
    const SS_EXIT     = "ic-exit-shown";    // sessionStorage: once per session
    const REDISPLAY_MS = 45 * 60 * 1000;   // 45 minutes before re-showing welcome

    const welcomeEl = document.querySelector(".js-ic-welcome");
    const exitEl    = document.querySelector(".js-ic-exit");
    if (!welcomeEl && !exitEl) return;

    /* ── helpers ── */
    const isSubscribed = () => localStorage.getItem(LS_SUB) === "1";
    const isOpen = () => !!document.querySelector(".ic-popup.is-open, .modal.is-open, .drawer.is-open");

    const markSubscribed = () => {
      localStorage.setItem(LS_SUB, "1");
      localStorage.removeItem(LS_WELCOME);
    };

    const openPopup = (el) => {
      if (!el || isOpen()) return;
      el.removeAttribute("hidden");
      requestAnimationFrame(() => {
        requestAnimationFrame(() => el.classList.add("is-open"));
      });
      el.focus();
    };

    const closePopup = (el) => {
      if (!el) return;
      el.classList.remove("is-open");
      setTimeout(() => el.setAttribute("hidden", ""), 450);
    };

    /* ── Close on backdrop click or close button (delegates to existing handler) ── */
    /* The existing js-popup-close class + dialog manager handles this already.     */
    /* We additionally close on backdrop click for ic-popup elements:               */
    [welcomeEl, exitEl].forEach(el => {
      if (!el) return;
      el.addEventListener("click", (e) => {
        if (e.target === el) closePopup(el); // clicked backdrop
      });
    });

    /* ── Track form submissions ── */
    document.querySelectorAll(".js-ic-form").forEach(form => {
      form.addEventListener("submit", () => {
        markSubscribed();
        // Give Shopify a moment to process, then close
        setTimeout(() => {
          closePopup(welcomeEl);
          closePopup(exitEl);
        }, 800);
      });
    });

    /* ════════════════════════════════════════════
     *  POPUP 1 — Welcome Offer
     * ════════════════════════════════════════════ */
    const shouldShowWelcome = () => {
      if (!welcomeEl) return false;
      if (isSubscribed()) return false;
      const lastShown = parseInt(localStorage.getItem(LS_WELCOME) || "0", 10);
      if (lastShown && Date.now() - lastShown < REDISPLAY_MS) return false;
      return true;
    };

    let welcomeShownThisSession = false;

    const triggerWelcome = () => {
      if (welcomeShownThisSession || !shouldShowWelcome() || isOpen()) return;
      welcomeShownThisSession = true;
      localStorage.setItem(LS_WELCOME, String(Date.now()));
      openPopup(welcomeEl);
    };

    if (shouldShowWelcome()) {
      // Trigger 1: timer (7s ± 2s jitter)
      const delay = 5000 + Math.random() * 4000;
      setTimeout(triggerWelcome, delay);

      // Trigger 2: scroll 25%
      const onScroll = () => {
        const scrolled = window.scrollY / (document.documentElement.scrollHeight - window.innerHeight);
        if (scrolled >= 0.25) {
          window.removeEventListener("scroll", onScroll, { passive: true });
          triggerWelcome();
        }
      };
      window.addEventListener("scroll", onScroll, { passive: true });
    }

    /* ════════════════════════════════════════════
     *  POPUP 2 — Exit Intent
     * ════════════════════════════════════════════ */
    const shouldShowExit = () => {
      if (!exitEl) return false;
      if (isSubscribed()) return false;
      if (sessionStorage.getItem(SS_EXIT)) return false;
      return true;
    };

    let exitBound = false;

    const triggerExit = () => {
      if (!shouldShowExit() || isOpen()) return;
      sessionStorage.setItem(SS_EXIT, "1");
      // If welcome is open, close it first and wait
      if (welcomeEl?.classList.contains("is-open")) {
        closePopup(welcomeEl);
        setTimeout(() => openPopup(exitEl), 600);
      } else {
        openPopup(exitEl);
      }
    };

    if (shouldShowExit()) {
      const isDesktop = () => window.innerWidth > 900;

      if (isDesktop()) {
        /* Desktop: mouse leaves to top of viewport */
        const onMouseLeave = (e) => {
          if (e.clientY <= 5 && e.relatedTarget === null) {
            document.removeEventListener("mouseleave", onMouseLeave);
            exitBound = true;
            triggerExit();
          }
        };
        document.addEventListener("mouseleave", onMouseLeave);
      } else {
        /* Mobile: inactivity after 75 seconds */
        let inactivityTimer;
        const resetTimer = () => {
          clearTimeout(inactivityTimer);
          inactivityTimer = setTimeout(() => {
            ["touchstart","scroll","click"].forEach(ev =>
              document.removeEventListener(ev, resetTimer)
            );
            triggerExit();
          }, 75000);
        };
        ["touchstart","scroll","click"].forEach(ev =>
          document.addEventListener(ev, resetTimer, { passive: true })
        );
        resetTimer();

        /* Mobile: also trigger on browser back navigation */
        history.pushState(null, "", location.href);
        window.addEventListener("popstate", () => triggerExit(), { once: true });
      }
    }
  })();


  /* ── Mega menu tabs & 2-second hover delay ─────────────────────── */
  const initMegaMenus = () => {
    let megaDelayTimer = null;

    document.querySelectorAll(".header__nav-item--mega").forEach((navItem) => {
      const megaMenu = navItem.querySelector(".mega-menu");

      const openMega = () => {
        if (megaDelayTimer) clearTimeout(megaDelayTimer);
        document.querySelectorAll(".header__nav-item--mega").forEach(n => n.classList.remove("is-hovered"));
        navItem.classList.add("is-hovered");
      };

      const closeMega = () => {
        if (megaDelayTimer) clearTimeout(megaDelayTimer);
        megaDelayTimer = setTimeout(() => {
          navItem.classList.remove("is-hovered");
        }, 1000); // 1-second window before vanishing
      };

      navItem.addEventListener("mouseenter", openMega);
      navItem.addEventListener("mouseleave", closeMega);
      if (megaMenu) {
        megaMenu.addEventListener("mouseenter", openMega);
        megaMenu.addEventListener("mouseleave", closeMega);
      }
    });

    document.querySelectorAll(".js-mega-trigger").forEach((trigger) => {
      const showMenu = () => {
        const parent = trigger.closest(".mega-menu");
        if (!parent) return;

        // Deactivate all sibling triggers
        parent.querySelectorAll(".js-mega-trigger").forEach((t) => t.classList.remove("is-active"));
        // Activate this trigger
        trigger.classList.add("is-active");

        // Hide all content lists
        parent.querySelectorAll(".js-mega-content").forEach((c) => {
          c.classList.remove("is-active");
          c.style.display = "none";
        });

        // Show target content list
        const targetId = trigger.dataset.target;
        const target = parent.querySelector(`#${targetId}`);
        if (target) {
          target.classList.add("is-active");
          target.style.display = "block";
        }
      };

      trigger.addEventListener("mouseenter", showMenu);
      trigger.addEventListener("click", (e) => {
        if (!trigger.classList.contains("is-active")) {
          e.preventDefault();
          showMenu();
        }
      });
    });
  };

  /* ── Collection Toolbar JS ─────────────────────────────────────── */
  const initCollectionToolbar = () => {
    const sortSelect = document.querySelector(".js-sort-by");
    if (sortSelect) {
      sortSelect.addEventListener("change", (e) => {
        const value = e.target.value;
        const url = new URL(window.location.href);
        url.searchParams.set("sort_by", value);
        window.location.href = url.toString();
      });
    }

    const filterBtn = document.querySelector(".js-filter-sidebar-toggle, .js-filter-drawer-toggle");
    const sidebar = document.querySelector("#collectionFilters");
    if (filterBtn && sidebar) {
      filterBtn.addEventListener("click", () => {
        const isHidden = sidebar.classList.toggle("is-hidden");
        const btnText = filterBtn.querySelector(".filter-toggle__text, .filter-btn__text");
        if (btnText) {
          btnText.textContent = isHidden ? "Show Filters" : "Hide Filters";
        }
      });
    }

    document.querySelectorAll(".js-grid-cols-btn").forEach((btn) => {
      btn.addEventListener("click", () => {
        const cols = btn.dataset.cols;
        const grid = document.querySelector(".js-collection-grid, .grid");
        if (!grid) return;
        document.querySelectorAll(".js-grid-cols-btn").forEach((b) => b.classList.remove("is-active"));
        btn.classList.add("is-active");

        if (cols === "2") {
          grid.classList.add("grid--2-col");
          grid.classList.remove("grid--4-col");
        } else {
          grid.classList.add("grid--4-col");
          grid.classList.remove("grid--2-col");
        }
      });
    });
  };

  /* ══════════ Init ══════════ */
  const init = () => {
    observeReveals();
    initCollection();
    initCollectionToolbar();
    initProductCards();
    bindVerticalWheelPassthrough();
    initGridSpacing();
    initSizeChart();
    initCustomizePanel();
    initPdpRhone();
    initMegaMenus();
    refreshCart();
  };
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();

  document.addEventListener("shopify:section:load", init);
})();
